// gallery.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface FolderNode {
  name: string;
  path: string;
  hasChildren: boolean;
  children?: FolderNode[]; // used in tree mode
}

export interface ImageItem {
  id?: string | number;   // ✅ يقبل الاتنين
  name: string;
  url: string;
  path: string;
  size: number;
  date: string;
  desc?: string;
  selected?: boolean;
}

export interface BrowseResponse {
  currentPath: string;
  folders: FolderNode[];
  images: ImageItem[];
  meta: {
    pageNumber: number;
    pageSize: number;
    totalImages: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
}

export interface BrowseResult {
  folders: FolderNode[];
  images: ImageItem[];
  currentPath: string;
}

@Injectable({ providedIn: 'root' })
export class GalleryService {

  private get base(): string {
    const base = localStorage.getItem('apiURL');
    if (!base) throw new Error('apiURL not ready');
    return base + '/api/gallery';
  }

  constructor(private http: HttpClient) {}

  // ============================================================
  // Browse — مع Cache Buster
  // ============================================================
  browse(
    path: string = '',
    pageNumber: number = 1,
    pageSize: number = 20,
    search?: string
  ): Observable<BrowseResponse> {

    let params = new HttpParams()
      .set('path', path)
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('search', search ?? '')
      .set('_t', Date.now().toString());   // ✅ Cache Buster

    return this.http.get<BrowseResponse>(`${this.base}/browse`, { params });
  }

  // ============================================================
  // Tree
  // ============================================================
  tree(): Observable<FolderNode> {
    return this.http.get<FolderNode>(`${this.base}/tree`);
  }

  // ============================================================
  // Upload
  // ============================================================
  upload(files: FileList, path: string): Observable<ImageItem[]> {
    const form = new FormData();
    Array.from(files).forEach(f => form.append('files', f, f.name));

    return this.http.post<ImageItem[]>(`${this.base}/upload`, form, {
      params: new HttpParams()
        .set('path', path)
        .set('_t', Date.now().toString()),
    });
  }

  // ============================================================
  // Rename Image
  // ============================================================
  renameImage(imagePath: string, newName: string, desc?: string): Observable<ImageItem> {
    console.log({
      path: imagePath,
      new_name: newName,
      desc,
    });

    return this.http.patch<ImageItem>(`${this.base}/rename-image`, {
      path: imagePath,
      new_name: newName,
      desc,
    });
  }

  // ============================================================
  // Delete Image
  // ============================================================
  deleteImage(imagePath: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/delete-image`, {
      params: new HttpParams()
        .set('path', imagePath)
        .set('_t', Date.now().toString()),
    });
  }

  // ============================================================
  // Create Folder
  // ============================================================
  createFolder(parentPath: string, name: string): Observable<FolderNode> {
    return this.http.post<FolderNode>(`${this.base}/create-folder`, null, {
      params: new HttpParams()
        .set('path', parentPath)
        .set('name', name),
    });
  }

  // ============================================================
  // Delete Folder
  // ============================================================
  deleteFolder(folderPath: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/delete-folder`, {
      params: new HttpParams().set('path', folderPath),
    });
  }

  // ============================================================
  // Format Size
  // ============================================================
  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  // ============================================================
  // Repair Uploads Links
  // ============================================================
  repairUploadsLinks(source: string): Observable<any> {
    return this.http.post(
      `${this.base}/repair-uploads-links?Source_Photos=${source}`,
      {}
    );
  }
}